/**
 * « Gérer une séance » (`move-session?liveId=`) : la séance du masteur, lue dans le compte comme
 * l'ancien écran (`findItemInListWithId(liveId, user.live_sessions)`), et ce que ses étapes lisent
 * de `static-data` (les raisons d'un déplacement). Les lectures propres à
 * l'écran (le décompte des absences, l'impact) restent dans `useTutorManageSessionData` (patron B).
 *
 * Un `createSelector` sur les tranches qu'il lit, l'argument lu champ par champ.
 */
import moment from "moment"
import { createSelector } from "reselect"

import {
    toTutorManagedSession,
    type ApiManagedLiveSession,
    type TutorManagedSession,
} from "../../../adapters/tutor/Lives/tutorManageSessionAdapter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import { selectAccount } from "../../shared/displayedStudent"

export type TutorManageSessionArgs = { liveId: string | number | null; dateTime?: string | null }

export type ReschedulingReasons = { one_occurrence?: string[] | null; all_occurrences?: string[] | null } | null

export type TutorManageSessionViewModel = {
    /** `null` : le compte n'est pas encore là (`loading`), ou la séance n'y est pas. */
    managed: TutorManagedSession | null
    loading: boolean
    /** Le compte, pour la règle des rattrapages, qui parcourt toutes ses séances. */
    liveSessions: ApiManagedLiveSession[]
    reschedulingReasons: ReschedulingReasons
}

type Account = { live_sessions?: ApiManagedLiveSession[] | null } | null

type ManageState = {
    data?: {
        staticData?: {
            school_subjects?: ApiSchoolSubject[] | null
            rescheduling_reasons?: ReschedulingReasons
        } | null
    }
}

const staticData = (state: unknown) => (state as ManageState)?.data?.staticData

const selectManage = createSelector(
    [
        // Les séances et la présence du compte, pas le compte : il change d'identité à chaque relecture.
        (state: unknown) => (selectAccount(state as never) as Account)?.live_sessions,
        (state: unknown) => selectAccount(state as never) != null,
        (state: unknown) => staticData(state)?.school_subjects ?? null,
        (state: unknown) => staticData(state)?.rescheduling_reasons ?? null,
        (_state: unknown, args: TutorManageSessionArgs) => args.liveId,
        (_state: unknown, args: TutorManageSessionArgs) => args.dateTime ?? null,
        () => new Date().toDateString(),
    ],
    (
        accountSessions,
        accountLoaded,
        schoolSubjects,
        reschedulingReasons,
        liveId,
        dateTime
    ): TutorManageSessionViewModel => {
        const liveSessions = accountSessions ?? []
        return {
            managed:
                liveId != null ? toTutorManagedSession(liveSessions, liveId, schoolSubjects, dateTime, moment()) : null,
            loading: !accountLoaded,
            liveSessions,
            reschedulingReasons,
        }
    }
)

export function tutorManageSessionViewModel(state: unknown, args: TutorManageSessionArgs): TutorManageSessionViewModel {
    return selectManage(state, args)
}
